import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import {
  GfFlyerItem,
  GfFlyerItemMetaData,
  GfImage,
  UpdateGfFlyerItemDto,
} from "@swagger/typescript-fetch-goflyer";
import { CreateFlyerModal } from ".";
import { Card, SubmitButton } from "./styled";
import KeyValueManager from "../../../components/KeyValueTypeListManager";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import { useHandleSaveFlyerItem } from "./handleSaveFlyerItem";
import { CheckBoxMui } from "../../../components/CheckBox";
import {
  FrontPageCheckboxWrapper,
  SyncUpdateHeader,
  CheckBoxWrapper,
} from "./styled";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { setWebsite } from "../../../../utils/getShowAt";
import CustomModal from "../../../components/CustomModal";
import { toastError } from "../../../../utils/pageUtil/toast";
import { parseRegularPricePerUnit } from "../../../../utils/regularPrice/parseRegularPricePerUnit";
import { useLastSelectedDepartment } from "../../../../hooks";

// Constants for unit conversion
const LB_TO_KG_RATIO = 2.2; // 1 kg = 2.2 lb (approx)

export interface UpdateGfFlyerItemDtoWithWebsiteSelection extends UpdateGfFlyerItemDto {
  storeWebsite?: boolean;
  goFlyerWebsite?: boolean;
}
interface CreateFlyerModalWithMetaDataProps {
  updateFlyerItem: UpdateGfFlyerItemDtoWithWebsiteSelection | undefined;
  setUpdateFlyerItem: React.Dispatch<
    React.SetStateAction<UpdateGfFlyerItemDtoWithWebsiteSelection | undefined>
  >;
  onSaveFlyerItem: (flyerItemResponse: GfFlyerItem) => void;
  handleOnBackgroundOverlayClick: () => void;
  onImageTagSaved?: (updatedImage: GfImage) => void;
  navigationDirection?: "left" | "right" | null;
  canNavigatePrevious?: boolean;
  canNavigateNext?: boolean;
  currentItemIndex?: number;
  totalItems?: number;
  onClick?: () => void;
  zIndex?: number;
  isResizableWindowOpen?: boolean;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
}

export const CreateFlyerModalWithMetaData: React.FC<
  CreateFlyerModalWithMetaDataProps
> = ({
  updateFlyerItem,
  setUpdateFlyerItem: _setUpdateFlyerItem,
  onSaveFlyerItem,
  handleOnBackgroundOverlayClick,
  onImageTagSaved,
  navigationDirection = null,
  canNavigatePrevious: _canNavigatePrevious = false,
  canNavigateNext: _canNavigateNext = false,
  currentItemIndex = -1,
  totalItems = 0,
  onClick,
  zIndex = 1300,
  isResizableWindowOpen = false,
  onNavigatePrevious,
  onNavigateNext,
}) => {
  const [draftFlyerItem, setDraftFlyerItem] = useState(updateFlyerItem);

  useEffect(() => {
    if (!updateFlyerItem) {
      setDraftFlyerItem(undefined);
      return;
    }

    // A brand new flyer item has no showAt yet, so default it to sync with the
    // GoFlyer website & app. Existing items always follow their saved showAt.
    const isNewFlyerItem = !updateFlyerItem?.id;
    const showAtSelection = isNewFlyerItem
      ? { goflyerWebsite: true, storeWebsite: false }
      : setWebsite(updateFlyerItem?.showAt);

    setDraftFlyerItem((prev) => {
      if (!prev || prev.id !== updateFlyerItem.id) {
        return {
          ...updateFlyerItem,
          storeWebsite: showAtSelection.storeWebsite,
          goFlyerWebsite: showAtSelection.goflyerWebsite,
        };
      }

      // Update categories if needed
      const needsCategoryUpdate =
        !prev.gfFlyerItemCategory?.length &&
        updateFlyerItem.gfFlyerItemCategory?.length;

      // Update showAt flags if they've changed
      const needsShowAtUpdate =
        prev.storeWebsite !== showAtSelection.storeWebsite ||
        prev.goFlyerWebsite !== showAtSelection.goflyerWebsite;

      if (needsCategoryUpdate || needsShowAtUpdate) {
        return {
          ...prev,
          ...(needsCategoryUpdate && {
            gfFlyerItemCategory: updateFlyerItem.gfFlyerItemCategory,
          }),
          ...(needsShowAtUpdate && {
            storeWebsite: showAtSelection.storeWebsite,
            goFlyerWebsite: showAtSelection.goflyerWebsite,
          }),
        };
      }

      return prev;
    });
  }, [updateFlyerItem]);

  const { t } = useTranslation();
  const handleSaveFlyerItem = useHandleSaveFlyerItem();
  const { recordSelection } = useLastSelectedDepartment(
    draftFlyerItem?.flyerItemList?.id,
  );
  const isSubmittingRef = React.useRef(false);

  const onUpdateKeyValues = (value?: GfFlyerItemMetaData[]) => {
    setDraftFlyerItem((prev) => ({
      ...prev,
      flyerItemMetaData: value,
    }));
  };

  const handleSubmit = useCallback(async () => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;

    try {
      if (draftFlyerItem?.flyerItemList?.id) {
        const ids = (draftFlyerItem?.gfFlyerItemCategory || []).map(
          (c) => c?.id,
        );
        recordSelection(ids);
      }
      const flyerItemResponse = await handleSaveFlyerItem(
        draftFlyerItem?.storeWebsite || false,
        draftFlyerItem?.goFlyerWebsite || false,
        draftFlyerItem,
      );
      if (!flyerItemResponse) {
        return;
      }
      flyerItemResponse && onSaveFlyerItem(flyerItemResponse);
    } finally {
      isSubmittingRef.current = false;
    }
  }, [draftFlyerItem, handleSaveFlyerItem, onSaveFlyerItem, recordSelection]);

  const handleCalculateSave = useCallback(() => {
    const salePrice = draftFlyerItem?.salePrice || 0;
    const regularPriceString = draftFlyerItem?.regularPriceString || "0";
    const quantity = draftFlyerItem?.quantity || 1; // Default to 1 if no quantity

    // Quantity and salePrice relationship:
    // Case A: quantity = 1 (or not set) -> salePrice is the price for 1 item
    //         Example: quantity=1, salePrice=5.99, regularPrice=7.99/ea
    //         Calculation: 7.99 - 5.99 = 2.00 savings
    //
    // Case B: quantity > 1 -> salePrice is the TOTAL price for all items in the quantity
    //         Example: quantity=2, salePrice=7, regularPrice=5.99/ea
    //         Calculation: salePricePerUnit = 7/2 = 3.50
    //                     savingsPerUnit = 5.99 - 3.50 = 2.49
    //                     totalSavings = 2.49 * 2 = 4.98
    //
    // A multi-buy regular price is per item too: "3 for 3.99" is 1.33 each.
    const regularPricePerUnit = parseRegularPricePerUnit(regularPriceString);

    if (!regularPricePerUnit) {
      toastError(t(messages.regularPriceRequiredForSavings()));
      return;
    }

    // Calculate sale price per unit when quantity is present
    // salePrice is the total price for the quantity
    const salePricePerUnit = salePrice / quantity;

    if (salePricePerUnit > regularPricePerUnit) {
      toastError(t(messages.salePriceCannotExceedRegularPrice()));
      return;
    }

    if (
      salePrice &&
      regularPricePerUnit &&
      regularPricePerUnit > salePricePerUnit
    ) {
      // Calculate savings per unit, then multiply by quantity for total savings
      const savingsPerUnit = regularPricePerUnit - salePricePerUnit;
      const totalSavings = savingsPerUnit * quantity;

      const newMetaData = [...(draftFlyerItem?.flyerItemMetaData || [])];

      const savingsIndex = newMetaData.findIndex(
        (item) => item.key === "savings" || item.key === "save",
      );

      if (savingsIndex !== -1) {
        newMetaData[savingsIndex] = {
          ...newMetaData[savingsIndex],
          key: "save",
          value: totalSavings.toFixed(2),
          type: "save",
        };
      } else {
        newMetaData.push({
          key: "save",
          value: totalSavings.toFixed(2),
          type: "save",
        } as GfFlyerItemMetaData);
      }

      setDraftFlyerItem((prev) => ({
        ...prev,
        flyerItemMetaData: newMetaData,
      }));
    }
  }, [draftFlyerItem, t]);

  const handleUnitConversion = useCallback(() => {
    if (!draftFlyerItem?.unit) return;

    const currentUnit = draftFlyerItem.unit.toLowerCase();

    let conversionRatio: number;
    let targetUnit: string;

    // Check for kg to lb conversion
    if (currentUnit === "kg") {
      // Price per kg to price per lb conversion using division by 2.2 factor
      conversionRatio = 1 / LB_TO_KG_RATIO;
      targetUnit = "lb";
    }
    // Check for lb to kg conversion
    else if (currentUnit === "lb" || currentUnit === "pound") {
      // Price per lb to price per kg conversion using multiplication by 2.2 factor
      conversionRatio = LB_TO_KG_RATIO;
      targetUnit = "kg";
    } else {
      console.log("Current unit is not supported for conversion.");
      return;
    }

    const newMetaData = [...(draftFlyerItem?.flyerItemMetaData || [])];

    // Round to exactly 2 decimal places
    const convert = (amount: number) =>
      (Math.round(amount * conversionRatio * 100) / 100).toFixed(2);

    const upsertConvertedRow = (targetKey: string, convertedValue: string) => {
      const existingIndex = newMetaData.findIndex(
        (item) =>
          item.key?.toLowerCase() === targetKey.toLowerCase() &&
          item.type?.toLowerCase() !== "price",
      );

      if (existingIndex !== -1) {
        newMetaData[existingIndex] = {
          ...newMetaData[existingIndex],
          key: targetKey,
          value: convertedValue,
          type: "unit",
        };
      } else {
        newMetaData.push({
          key: targetKey,
          value: convertedValue,
          type: "unit",
        } as GfFlyerItemMetaData);
      }
    };

    // 1. Convert every "price" row (e.g. "clean" / "no clean") into the target
    // unit. e.g. { key: "no clean", value: "12.23", type: "price" }
    //        -> { key: "no clean kg", value: <converted>, type: "unit" }
    newMetaData
      .filter((item) => item.type?.toLowerCase() === "price")
      .forEach((priceEntry) => {
        const numericValue = parseFloat(
          (priceEntry.value ?? "").toString().replace(/[^0-9.]/g, "") || "0",
        );
        if (!numericValue) return;

        upsertConvertedRow(
          `${priceEntry.key ?? ""} ${targetUnit}`.trim(),
          convert(numericValue),
        );
      });

    // 2. Convert the sale price as well. This runs alongside the "price" rows
    // above, not instead of them, so clean / no clean / sale price are all
    // converted by a single click.
    if (draftFlyerItem?.salePrice) {
      upsertConvertedRow(targetUnit, convert(draftFlyerItem.salePrice));
    }

    setDraftFlyerItem((prev) => ({
      ...prev,
      flyerItemMetaData: newMetaData,
    }));
  }, [draftFlyerItem]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (document.body.dataset.imageEditorActive === "true") {
        return;
      }

      const targetElement = event.target as HTMLElement | null;
      if (targetElement?.closest('[data-stop-global-enter="true"]')) {
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        handleSubmit();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit]);

  // Check if the unit is "lb", "pound", or "kg" for displaying the Convert Unit button
  const isConvertibleUnit = ["lb", "pound", "kg"].includes(
    draftFlyerItem?.unit?.toLowerCase() ?? "",
  );

  // "price" rows (e.g. clean / no clean) are convertible on their own, so the
  // button stays available even when no sale price has been entered.
  const hasConvertiblePriceMetaData = (
    draftFlyerItem?.flyerItemMetaData || []
  ).some((item) => item.type?.toLowerCase() === "price");

  const shouldShowConvertUnitButton =
    isConvertibleUnit &&
    ((draftFlyerItem?.salePrice ?? 0) > 0 || hasConvertiblePriceMetaData);

  // Check if regularPriceString exists for displaying the Calculate Save button
  const shouldShowCalculateSaveButton = !!draftFlyerItem?.regularPriceString;

  return (
    <CustomModal
      onClose={handleOnBackgroundOverlayClick}
      dataTestId="create-flyer-item-modal"
      navigationDirection={navigationDirection}
      animate={true}
      overlayStyles={{
        zIndex,
        ...(isResizableWindowOpen && {
          backdropFilter: "none",
          pointerEvents: "none",
        }),
      }}
      wrapperStyles={{
        ...(isResizableWindowOpen && {
          pointerEvents: "auto",
        }),
      }}
      footer={
        onNavigatePrevious || onNavigateNext ? (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={{ xs: 3, sm: 2 }}
            sx={{
              mb: { xs: "4px", sm: "8px" },
              mt: "-50px",
            }}
          >
            <IconButton
              onClick={onNavigatePrevious}
              disabled={!_canNavigatePrevious}
              size="medium"
              data-testid="navigate-previous-button"
              sx={{
                backgroundColor: "white",
                border: "1px solid",
                borderColor: _canNavigatePrevious
                  ? "#ff8038"
                  : "rgba(255,255,255,0.3)",
                color: _canNavigatePrevious
                  ? "#ff8038"
                  : "rgba(255,255,255,0.5)",
                width: { xs: 48, sm: 40 },
                height: { xs: 48, sm: 40 },
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.2)",
                },
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: { xs: 20, sm: 16 } }} />
            </IconButton>
            <IconButton
              onClick={onNavigateNext}
              disabled={!_canNavigateNext}
              size="medium"
              data-testid="navigate-next-button"
              sx={{
                backgroundColor: "white",
                border: "1px solid",
                borderColor: _canNavigateNext
                  ? "#ff8038"
                  : "rgba(255,255,255,0.3)",
                color: _canNavigateNext ? "#ff8038" : "rgba(255,255,255,0.5)",
                width: { xs: 48, sm: 40 },
                height: { xs: 48, sm: 40 },
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.2)",
                },
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: { xs: 20, sm: 16 } }} />
            </IconButton>
          </Stack>
        ) : undefined
      }
    >
      <Card onClick={onClick}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "column", lg: "row" },
            justifyContent: "center",
            alignItems: "stretch",
            width: "100%",
            height: "100%",
            maxWidth: "100%",
          }}
        >
          <CreateFlyerModal
            key={draftFlyerItem?.id || "new"}
            updateFlyerItem={draftFlyerItem}
            setUpdateFlyerItem={setDraftFlyerItem}
            onImageTagSaved={onImageTagSaved}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "100%",
              maxWidth: { xs: "100%", md: "100%", lg: "450px" },
              pl: { xs: 0, lg: "24px" },
            }}
          >
            <div style={{ width: "100%" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mt={{ xs: "10px", md: "10px", lg: "0px" }}
                mb={"10px"}
                mr={"30px"}
              >
                <Typography>{t(messages.MetaData())}</Typography>
                {totalItems > 0 && currentItemIndex >= 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                    }}
                  >
                    {currentItemIndex + 1} / {totalItems}
                  </Typography>
                )}
              </Stack>
              <KeyValueManager<GfFlyerItemMetaData>
                keyValues={draftFlyerItem?.flyerItemMetaData}
                onUpdateKeyValues={onUpdateKeyValues}
              />

              {(shouldShowCalculateSaveButton ||
                shouldShowConvertUnitButton) && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    marginTop: 1,
                  }}
                >
                  {shouldShowCalculateSaveButton && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCalculateSave}
                      data-testid="calculate-save-button"
                      title={t(messages.Calculate_Save_Tooltip())}
                      sx={{
                        borderColor: "#ff813a",
                        color: "#ff813a",
                        fontSize: "12px",
                        padding: "2px 10px",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "rgba(255, 129, 58, 0.04)",
                          borderColor: "#ff813a",
                        },
                      }}
                    >
                      {t(messages.Calculate_Save())}
                    </Button>
                  )}

                  {shouldShowConvertUnitButton && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleUnitConversion}
                      data-testid="convert-unit-button"
                      title={t(messages.Convert_Unit_Tooltip())}
                      sx={{
                        borderColor: "#ff813a",
                        color: "#ff813a",
                        fontSize: "12px",
                        padding: "2px 10px",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "rgba(255, 129, 58, 0.04)",
                          borderColor: "#ff813a",
                        },
                      }}
                    >
                      {t(messages.Convert_Unit())}
                    </Button>
                  )}
                </Stack>
              )}
            </div>
            <div style={{ width: "100%" }}>
              <div
                style={{
                  marginTop: "4px",
                }}
              >
                <FrontPageCheckboxWrapper>
                  <CheckBoxMui
                    title={`${t(messages.Front_Page())}`}
                    onChange={(
                      // eslint-disable-next-line no-unused-vars
                      event: React.ChangeEvent<HTMLInputElement>,
                      checked: boolean,
                    ) => {
                      setDraftFlyerItem((prevValue) => ({
                        ...prevValue,
                        topSale: checked,
                        goFlyerWebsite: checked
                          ? true
                          : prevValue?.goFlyerWebsite,
                      }));
                    }}
                    value={draftFlyerItem?.topSale}
                    dataTestId="front-page-check"
                    tooltip={t(
                      messages.DISPLAY_THIS_ITEM_PROMINENTLY_ON_THE_FRONT_PAGE_OF_THE_FLYER(),
                    )}
                    tooltipPlacement="right"
                  />
                </FrontPageCheckboxWrapper>
              </div>
              <div>
                <SyncUpdateHeader>
                  {t(messages.Sync_Update_to())}:
                </SyncUpdateHeader>
                <CheckBoxWrapper>
                  <CheckBoxMui
                    FormControlStyle={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                      },
                    }}
                    title={t(messages.Goflyer_website_APP()) as string}
                    value={draftFlyerItem?.goFlyerWebsite}
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement>,
                      checked: boolean,
                    ) => {
                      setDraftFlyerItem((prevValue) => ({
                        ...prevValue,
                        goFlyerWebsite: checked,
                      }));
                    }}
                    dataTestId="goflyer-website-check"
                    tooltip={t(
                      messages.SYNC_THIS_ITEM_TO_THE_GO_FLYER_PLATFORM_WEBSITE_AND_MOBILE_APP(),
                    )}
                    tooltipPlacement="top"
                  />
                  <CheckBoxMui
                    FormControlStyle={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                      },
                    }}
                    title={t(messages.Store_website_APP()) as string}
                    value={draftFlyerItem?.storeWebsite}
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement>,
                      checked: boolean,
                    ) => {
                      setDraftFlyerItem((prevValue) => ({
                        ...prevValue,
                        storeWebsite: checked,
                      }));
                    }}
                    dataTestId="store-website-check"
                    tooltip={t(
                      messages.SYNC_THIS_ITEM_TO_THE_STORE_S_WEBSITE_AND_MOBILE_APP(),
                    )}
                    tooltipPlacement="top"
                  />
                </CheckBoxWrapper>
              </div>
              <Stack
                direction={"row"}
                justifyContent={"flex-end"}
                width="100%"
                mt={"5px"}
              >
                <SubmitButton
                  onClick={handleSubmit}
                  data-testid="submit-button"
                  style={{ width: "100%" }}
                >
                  {draftFlyerItem?.id
                    ? t(messages.Update())
                    : `${t(messages.Submit())}`}
                </SubmitButton>
              </Stack>
            </div>
          </Box>
        </Box>
      </Card>
    </CustomModal>
  );
};

export default CreateFlyerModalWithMetaData;
